import _get from 'lodash/get';
import _isNumber from 'lodash/isNumber';

import { MFA_METHODS, MFA_STATUS } from 'constants/mfa';

import INITIAL_STATE from './initialState';

/**
 * Store the MFA session built from the authenticate API response.
 *
 * @method setMFASession
 * @param  {Object}      state  The current state
 * @param  {Object}      action The dispatched action
 * @return {Object}             The updated state
 */
export const setMFASession = (state, action) => {
    const payload = _get(action, 'payload', {});

    return {
        ...state,
        allowedMethods: _get(payload, 'allowedMethods', []),
        enrollmentError: null,
        enrollmentStatus: '',
        error: null,
        isRegistering: false,
        isVerifying: false,
        loginContext: _get(payload, 'loginContext', {}),
        method: _get(payload, 'method', ''),
        mfaToken: _get(payload, 'mfaToken', ''),
        qrCode: '',
        remainingAttempts: null,
        secret: '',
        status: _get(payload, 'status', ''),
    };
};

/**
 * Clear the MFA session, i.e. reset the 'mfa' state.
 *
 * @method clearMFASession
 * @return {Object} The initial state
 */
export const clearMFASession = () => ({ ...INITIAL_STATE });

/**
 * Clear the error of the last verification attempt.
 *
 * NOTE: the `mfaToken` and the attempts left are kept, the challenge is still
 * the same one and its attempts are owned by the API.
 *
 * @method clearMFAError
 * @param  {Object}     state The current state
 * @return {Object}           The updated state
 */
export const clearMFAError = state => ({
    ...state,
    error: null,
});

/**
 * Clear the error of the last enrollment attempt.
 *
 * @method clearMFAEnrollmentError
 * @param  {Object}                state The current state
 * @return {Object}                      The updated state
 */
export const clearMFAEnrollmentError = state => ({
    ...state,
    enrollmentError: null,
});

/**
 * Flag the enrollment request as in flight.
 *
 * NOTE: the data of an earlier enrollment is dropped, only the enrollment the
 * request answers with is ever rendered.
 *
 * @method registerMFAMethodRequest
 * @param  {Object}                 state The current state
 * @return {Object}                       The updated state
 */
export const registerMFAMethodRequest = state => ({
    ...state,
    enrollmentError: null,
    enrollmentStatus: '',
    isRegistering: true,
    qrCode: '',
    secret: '',
});

/**
 * Store the enrollment the register API started.
 *
 * NOTE: the `secret` and the `qrCode` are kept in this state alone, which is
 * never persisted, so they leave no trace once the flow is over.
 *
 * @method registerMFAMethodSuccess
 * @param  {Object}                 state  The current state
 * @param  {Object}                 action The dispatched action
 * @return {Object}                        The updated state
 */
export const registerMFAMethodSuccess = (state, action) => {
    const payload = _get(action, 'payload', {});

    return {
        ...state,
        enrollmentError: null,
        enrollmentStatus: _get(payload, 'status', ''),
        isRegistering: false,
        qrCode: _get(payload, 'qrCode', ''),
        secret: _get(payload, 'secret', ''),
    };
};

/**
 * Handle an enrollment that could not be started.
 *
 * NOTE: the raw error is kept as is, it carries the `errorCode` the screen
 * derives its error state from.
 *
 * @method registerMFAMethodError
 * @param  {Object}               state  The current state
 * @param  {Object}               action The dispatched action
 * @return {Object}                      The updated state
 */
export const registerMFAMethodError = (state, action) => ({
    ...state,
    enrollmentError: _get(action, 'payload', null),
    isRegistering: false,
});

/**
 * Flag the verification request as in flight.
 *
 * @method verifyMFACodeRequest
 * @param  {Object}             state The current state
 * @return {Object}                   The updated state
 */
export const verifyMFACodeRequest = state => ({
    ...state,
    error: null,
    isVerifying: true,
});

/**
 * Handle a verified code.
 *
 * NOTE: the `mfaToken`, the `secret` and the `qrCode` are dropped right away,
 * the challenge is over. A verification that completes an enrollment keeps the
 * enrolled `method`, the `loginContext` and the OAuth token set the API answered
 * with alone: the success screen renders the first and completes the sign in
 * with the others, so they must survive the reset.
 *
 * @method verifyMFACodeSuccess
 * @param  {Object}             state  The current state
 * @param  {Object}             action The dispatched action
 * @return {Object}                    The updated state
 */
export const verifyMFACodeSuccess = (state, action) => {
    const isEnrollmentComplete = _get(state, 'status', '') === MFA_STATUS.MFA_REQUIRED;

    return {
        ...INITIAL_STATE,
        isEnrollmentComplete,
        loginContext: isEnrollmentComplete ? _get(state, 'loginContext', {}) : {},
        method: isEnrollmentComplete ? _get(state, 'method', '') || MFA_METHODS.TOTP : '',
        pendingToken: isEnrollmentComplete ? _get(action, 'payload.token', null) : null,
    };
};

/**
 * Flag the sign in of a completed enrollment as in flight.
 *
 * @method completeMFAEnrollmentRequest
 * @param  {Object}                     state The current state
 * @return {Object}                           The updated state
 */
export const completeMFAEnrollmentRequest = state => ({
    ...state,
    isCompletingEnrollment: true,
});

/**
 * Handle a failed verification attempt.
 *
 * NOTE: the raw error is kept as is, it carries the `errorCode` the screen
 * derives its error state from. The `remainingAttempts` of the response is
 * stored on its own, so the attempts left survive the error being cleared.
 *
 * @method verifyMFACodeError
 * @param  {Object}           state  The current state
 * @param  {Object}           action The dispatched action
 * @return {Object}                  The updated state
 */
export const verifyMFACodeError = (state, action) => {
    const error = _get(action, 'payload', null);
    const remainingAttempts = _get(error, 'response.data.remainingAttempts', null);

    return {
        ...state,
        error,
        isVerifying: false,
        remainingAttempts: _isNumber(remainingAttempts)
            ? remainingAttempts
            : _get(state, 'remainingAttempts', null),
    };
};
